import React, { useEffect, useState, useContext } from 'react';
import { Box, Stack, Typography, Container } from '@mui/material';
import PropTypes from 'prop-types';
import AccountDetails from './AccountDetails';
import TermsAndConditions from './TermsAndCondition';
import MaintainUsers from './MaintainUsers';
import MaintainTraders from './MaintainTraders';
import SettingHeader from './SettingHeader';
import Subscription from './Subscription/Subscription';
import { useLocation, useSearchParams } from 'react-router-dom';
import SideNav from './SideNav';
import { StoreContext } from '../../components/Store';
import { getAccountDetails } from '../../api/settings';
import { is360PayStaff } from '../../utils/roles';

import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import TraderIcon from '@mui/icons-material/Business';
import TermsIcon from '@mui/icons-material/Gavel';
import SubscriptionIcon from '@mui/icons-material/AccountBalanceWallet';

function TabPanel(props) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      className="customer-settings-screen-tab"
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default function CustomerSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const store = useContext(StoreContext);
  const [value, setValue] = useState(0);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [customerType, setCustomerType] = useState(null);

  const showSubscriptionTab = customerType === 10 || customerType === 60;
  const companyId =
    searchParams.get('companyId') ||
    state?.res?.companyId ||
    store.accountDetails[0]?.companyId ||
    store.eoriCompany[0] ||
    '';

  const tabData = [
    {
      label: 'Account Settings',
      id: 'account-settings',
      icon: <SettingsIcon />,
      page: <AccountDetails companyId={companyId} setCustomerType={setCustomerType} />,
    },
    ...(showSubscriptionTab
      ? [
          {
            label: 'Subscription',
            heading: 'Subscription Details',
            id: 'subscription',
            icon: <SubscriptionIcon />,
            page: <Subscription companyId={companyId} isVisible={value === 1} />,
          },
        ]
      : []),
    {
      label: 'Terms and Conditions',
      id: 'terms-and-conditions',
      icon: <TermsIcon />,
      page: <TermsAndConditions companyId={companyId} />,
    },
    {
      label: 'Maintain Users',
      heading: 'Users',
      id: 'maintain-users',
      icon: <PersonIcon />,
      page: <MaintainUsers companyId={companyId} isReadOnly={isReadOnly} customerType={customerType} />,
    },
    {
      label: 'Maintain Traders',
      heading: 'Trader Data',
      id: 'maintain-traders',
      icon: <TraderIcon />,
      page: <MaintainTraders companyId={companyId} isReadOnly={isReadOnly} />,
    },
  ];

  const heading = tabData[value]?.heading || tabData[value]?.label;

  const tabIds = tabData.map((t) => t.id).join(',');

  // Initialize tab from URL on mount and when tab structure changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      const index = tabData.findIndex((tab) => tab.id === tabFromUrl);
      if (index >= 0) {
        setValue(index);
      }
    }
  }, [searchParams, tabIds]);

  // Sync companyId to URL when landing with state (so it persists on refresh)
  useEffect(() => {
    if (state?.res?.companyId && !searchParams.get('companyId')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('companyId', state.res.companyId);
        return next;
      });
    }
  }, [state?.res?.companyId, searchParams, setSearchParams]);

  const handleTabChange = (newValue) => {
    setValue(newValue);
    const tabId = tabData[newValue]?.id;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tabId) {
        next.set('tab', tabId);
      }
      if (companyId) {
        next.set('companyId', companyId);
      }
      return next;
    });
  };

  useEffect(() => {
    const customerType = store.accountDetails[0]?.customerTypeId;
    setIsReadOnly(customerType === 40 || is360PayStaff());
    setCustomerType(customerType);
  }, [store.accountDetails[0]]);

  // Fetch account details when companyId is from URL and we're not on Account Settings tab
  // (so header shows company name and customerType is available for tab structure)
  useEffect(() => {
    const companyIdFromUrl = searchParams.get('companyId');
    if (companyIdFromUrl) {
      getAccountDetails(companyIdFromUrl)
        .then((data) => {
          store.accountDetails[1](data);
        })
        .catch(() => {
          // Ignore - Account Settings tab will handle if user navigates there
        });
    }
  }, [searchParams]);

  return (
    <Container maxWidth="xl" sx={{ px: 4, mt: 4, mb: 4 }}>
      <Stack direction="row" gap={2} className="global-wrapper">
        <SettingHeader heading={heading} />

        <div className="display-as-row-centered settings-row">
          <SideNav heading="Settings" tabs={tabData} setValue={handleTabChange} value={value} />

          {tabData.map((tab, index) => (
            <TabPanel key={tab.id} index={index} value={value}>
              {tab.page}
            </TabPanel>
          ))}
        </div>
      </Stack>
    </Container>
  );
}

TabPanel.propTypes = {
  children: PropTypes.func,
  value: PropTypes.string,
  index: PropTypes.string,
};
